<template>
  <div>
    <div class="text-center">
      <p class="red-text m-2"><i>{{$t('Account.modal.asterisk_required')}}</i></p>
    </div>
    <div class="main">
      <!-- Account Info -->
      <div class="sub-container mx-4">
        <div>
          <custom-google-autocomplete
            :accountInfo="userInfo"
            @updateEmptyGoogleFields="setEmptyGoogleFields"
            v-show="newStatus === status.edit">
          </custom-google-autocomplete>
        </div>

        <div>
          <label class="mt-2" for="name">{{$t('Account.modal.company_name')}} <strong class="red-text">*</strong></label>
          <regular-input
            :class="{'red' : isUserInfoFilled.name}"
            id="name"
            :placeholder="$t('Account.modal.company_name')"
            :requiredField="false"
            :value="userInfo.name"
            @input="userInfo.name = $event"
            :disabled="newStatus !== status.edit"
          />
        </div>

        <div>
          <label class="mt-2" for="address">{{$t('Account.modal.address')}} <strong class="red-text">*</strong></label>
          <regular-input
            :class="{'red' : isUserInfoFilled.address}"
            id="address"
            :placeholder="$t('Account.modal.address')"
            :requiredField="false"
            :value="userInfo.address"
            :disabled="isGoogleInfoEmpty.address"
            @input="userInfo.address = $event"
          />
        </div>

        <div>
          <label class="mt-2" for="city">{{$t('Account.modal.city')}} <strong class="red-text">*</strong></label>
          <regular-input
            :class="{'red' : isUserInfoFilled.city}"
            id="city"
            :placeholder="$t('Account.modal.city')"
            :requiredField="false"
            :value="userInfo.city"
            :disabled="isGoogleInfoEmpty.city"
            @input="userInfo.city = $event"
          />
        </div>

        <div>
          <label class="mt-2" for="postcode">{{$t('Account.modal.postcode')}} <strong class="red-text">*</strong></label>
          <regular-input
            :class="{'red' : isUserInfoFilled.postcode}"
            id="postcode"
            :placeholder="$t('Account.modal.postcode')"
            :requiredField="false"
            :value="userInfo.postcode"
            :disabled="isGoogleInfoEmpty.postcode"
            @input="userInfo.postcode = $event"
          />
        </div>

        <div>
          <label class="mt-2" for="province">{{$t('Account.modal.province')}} <strong class="red-text">*</strong></label>
          <regular-input
            :class="{'red' : isUserInfoFilled.province}"
            id="province"
            :placeholder="$t('Account.modal.province')"
            :requiredField="false"
            :value="userInfo.province"
            :disabled="isGoogleInfoEmpty.province"
            @input="userInfo.province = $event"
          />
        </div>

        <div>
          <label class="mt-2" for="country">{{$t('Account.modal.country')}} <strong class="red-text">*</strong></label>
          <regular-input
            :class="{'red' : isUserInfoFilled.country}"
            id="country"
            :placeholder="$t('Account.modal.country')"
            :requiredField="false"
            :value="userInfo.country"
            :disabled="isGoogleInfoEmpty.country"
            @input="userInfo.country = $event"
          />
        </div>
      </div>

      <!-- Non-Account Info -->
      <div class="sub-container mx-4">
        <div>
          <label class="mt-2" for="phone">{{$t('Account.modal.phone')}} <strong class="red-text">*</strong></label>
          <regular-input
            :type="'tel'"
            :class="{'red' : isUserInfoFilled.phone}"
            id="phone"
            :placeholder="$t('Account.modal.phone')"
            :requiredField="false"
            :disabled="newStatus !== status.edit"
            :value="userInfo.phone"
            @input="userInfo.phone = $event"
          />
        </div>

        <div>
          <label class="mt-2" for="fax">{{$t('Account.modal.fax')}}</label>
          <regular-input
            id="fax"
            :placeholder="$t('Account.modal.fax')"
            :requiredField="false"
            :disabled="newStatus !== status.edit"
            :value="userInfo.fax"
            @input="userInfo.fax = $event"
        />
        </div>

        <div>
          <label class="mt-2" for="sageID">{{$t('Account.modal.sageid')}}</label>
          <regular-input
            id="sageID"
            :placeholder="$t('Account.modal.sageid')"
            :requiredField="false"
            :disabled="newStatus !== status.edit"
            :value="userInfo.sageID"
            @input="userInfo.sageID = $event"/>
        </div>
      </div>

      <!-- Login Info + Extra -->
      <div class="sub-container mx-4">
        <div>
          <label class="mt-2" for="email">{{$t('Account.modal.email')}}
            <strong class="red-text">*</strong>
            <hover-tool-tip
              :text="$t('Account.modal.email_format_required')"
              :top="true"
              :left="true"
              :clickable="true"
            />
          </label>
          <regular-input
            :type="'email'"
            :class="{'red' : isUserInfoFilled.email}"
            id="email"
            :placeholder="$t('Account.modal.email')"
            :requiredField="false"
            :value="userInfo.email"
            :disabled="newStatus !== status.edit"
        />
        </div>

        <div v-show="newStatus === status.edit">
          <label class="mt-2" for="password">{{$t('Account.modal.password')}}
            <strong class="red-text">*</strong>
            <hover-tool-tip
              :text="$t('Account.modal.password_length_required')"
              :top="true"
              :left="true"
              :clickable="true"
            />
            </label>
          <regular-input
            :class="{'red' : isUserInfoFilled.password}"
            id="password"
            :placeholder="$t('Account.modal.password')"
            :requiredField="false"
            :value="userInfo.password"
            @input="userInfo.password = $event"
            :disabled="newStatus !== status.edit"/>
        </div>

        <div v-show="false">
          <label class="mt-2" for="transporter">{{$t('Account.modal.transp_list')}}</label>
          <multi-select
            isDisabled
            :options="transpSelectOptions"
            :selected-options="transpLinkList"
            @select="onTransporterSelect"
            :placeholder="$t('Account.modal.transp_select')"
          >
          </multi-select>

          <label for="newTransporters" v-if="transpLinkList.length <= 1"><strong>{{$t('Account.modal.transp_list_new')}}:</strong></label>
          <label for="newTransporters" v-if="transpLinkList.length > 1"><strong>{{$t('Account.modal.transp_list_new_multiple')}}:</strong></label>
          <div name="newTransporters" class="green-text" v-for="newTransporter in transpLinkList" :key="newTransporter.name">
            <span>- {{newTransporter.text}}</span>
          </div>

          <br>

          <!-- <label class="mt-2" for="current-trans-links"><strong><i>{{$t('Account.modal.current_list')}}</i></strong></label>
          <div id="current-trans-links" v-for="(transporter, transporterUid) in getTransporterLinks" :key="transporterUid">
            <p class="m-0" :id="transporterUid">
              <span>{{transporter}}</span>
            </p>
          </div> -->
        </div>
        <!-- <span v-if="getTransporterLinks <= 0">{{ $t('Account.modal.empty_transp_list') }}</span> -->
        <div :class="{'loading-container' : isLoading}">
          <div v-show="isLoading" class="loader"></div>
          <div v-show="!isLoading"></div>
        </div>

        <div class="mt-4 pt-2">
          <p class="red-text" v-show="errorMessage"><i><strong>{{ errorMessage }} </strong></i></p>
          <button v-show="newStatus === status.denied" class="w-100 btn btn-kne-orange" @click="onDenyAccount()">{{$t('Account.modal.button_deny_account')}}</button>
          <button v-show="newStatus === status.approved" class="w-100 btn btn-kne-orange" @click="onApproveAccount()">{{$t('Account.modal.button_approve_account')}}</button>
          <button v-show="newStatus === status.edit" class="w-100 btn btn-kne-orange" @click="onEditAccount()">{{$t('Account.modal.button_confirm_edit')}}</button>
          <!-- <button class="w-100 btn btn-kne-orange" @click="onDenyAccount()">{{$t('Account.modal.button_deny_account')}}</button>
          <button class="w-100 btn btn-kne-orange" @click="onApproveAccount()">{{$t('Account.modal.button_approve_account')}}</button>
          <button class="w-100 btn btn-kne-orange" @click="onEditAccount()">{{$t('Account.modal.button_edit_account')}}</button> -->
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import RegularInput from '../../basic-components/regularInput.vue'
import Navbar from '../../interface-components/NavBar.vue'
import CustomGoogleAutocomplete from '../Custom-packages/Custom-GoogleAutocomplete.vue'
import { MultiSelect } from 'vue-search-select'
import HoverToolTip from '../../basic-components/HoverToolTip.vue'

import 'vue-search-select/dist/VueSearchSelect.css'
import { db } from '../../../../firebase.js'

export default {
  name: 'PendingAccountForm',

  props: {
    edittedAccount: {
      type: Object,
      required: false
    },

    isEditMode: {
      type: Boolean,
      required: true
    },

    transporterList: {
      type: [Array, Boolean, String],
      required: false
    },

    newStatus: {
      type: String,
      required: true
    },

    companyUID: {
      type: String,
      required: false
    }
  },

  components: {
    RegularInput,
    Navbar,
    CustomGoogleAutocomplete,
    MultiSelect,
    HoverToolTip
  },

  data () {
    return {
      userInfo: {
        name: '',
        address: '',
        city: '',
        postcode: '',
        province: '',
        country: '',
        phone: '',
        fax: '',
        email: '',
        password: '',
        sageID: '',
        transporterUid: '',
        isActive: true
      },
      isUserInfoFilled: { // This serves as setting required fields and changing missing fields style
        // name: false,
        address: false,
        city: false,
        postcode: false,
        province: false,
        country: false
        // phone: false,
        // email: false,
        // password: false
      },
      isGoogleInfoEmpty: {
        address: true,
        city: true,
        postcode: true,
        province: true,
        country: true
      },
      transpLinkList: [],
      memoryTransptLinkList: {},
      errorMessage: '',
      isLoading: false,
      status: {
        approved: 'Approved',
        pending: 'Pending',
        denied: 'Denied',
        edit: 'Edit'
      }
    }
  },

  created () {
    this.userInfoIsEdit()
    this.setTransporterListFromParent(this.companyUID)
  },

  methods: {
    /** Approve account */
    onApproveAccount () {
      if (!this.initiateFormProcess()) return

      this.isLoading = true
      const EAKey = this.edittedAccount['.key']

      const newAccountDetails = this.setEdittedFields()
      const transporterDealerLinkList = this.setTransporterDealerLinkList(true)

      this.createAccountInFirebase(EAKey)
      this.saveDealerAccountDetails(newAccountDetails, EAKey)
      this.saveTransporterDealerLinkList(transporterDealerLinkList, EAKey, true)
      this.updatePendingAccountFB(EAKey, this.status.approved)
      this.sendEmail(this.userInfo.email)

      /** @emits TransporterAccounts.vue */
      this.$emit('closeEditModal', false)
    },

    /** On deny account, remove links to certain accounts */
    onDenyAccount () {
      if (!this.initiateFormProcess()) return

      this.isLoading = true
      const EAKey = this.edittedAccount['.key']

      const newAccountDetails = this.setEdittedFields(this.status.denied)
      this.saveDealerAccountDetails(newAccountDetails, EAKey)

      const transporterDealerLinkList = this.setTransporterDealerLinkList(false)

      this.saveTransporterDealerLinkList(transporterDealerLinkList, EAKey, false)
      this.updatePendingAccountFB(EAKey, this.status.denied)

      /** @emits TransporterAccounts.vue */
      this.$emit('closeEditModal', false)
    },

    onEditAccount () {
      if (!this.initiateFormProcess()) return

      this.isLoading = true
      const EAKey = this.edittedAccount['.key']

      const newAccountDetails = this.setEdittedFields()

      this.updateAccountInfoFB(EAKey, newAccountDetails)

      /** @emits TransporterAccounts.vue */
      this.$emit('closeEditModal', false)
    },

    /** Set the edit object @param updatedFields to be passed into @function onApproveAccount() */
    setEdittedFields (status) {
      const uInfo = this.userInfo
      let liste = this.setTransporterLinkList()

      let updatedFields = {
        'name': uInfo.name,
        'address': uInfo.address,
        'city': uInfo.city,
        'postcode': uInfo.postcode,
        'province': uInfo.province,
        'country': uInfo.country,
        'phone': uInfo.phone,
        'fax': uInfo.fax,
        'sageID': uInfo.sageID,
        'liste': liste,
        'companyUID': this.companyUID
      }

      return updatedFields
    },

    /** Approved accounts have their @param companyUid
     * set to the logged in account that approved them
    */
    setTransporterLinkList () {
      if (!this.userInfo.liste) this.userInfo.liste = {}
      let transporterList = this.userInfo.liste

      this.transpLinkList.forEach(transporter => {
        let tempObject = {}
        let uid = ''

        uid = transporter.uid

        tempObject = {
          [uid]: transporter.text
        }

        transporterList = Object.assign(this.userInfo.liste, tempObject)
      })

      return transporterList
    },

    /**  */
    createAccountInFirebase (EAKey) {
      let info = this.userInfo
      info['.key'] = ''
      delete info['.key']

      db.ref('users').child('dealers').child(EAKey).set(info).then(() => {
        db.ref('users').child('dealersman').child(EAKey).set({
          'company': EAKey,
          'infos': {
            'language': 'en'
          },
          'role': 'Admin'
        })
      })
    },

    /** Performs basic checks before continuing the form process */
    initiateFormProcess () {
      this.errorMessage = ''
      let isGoodToGo

      let areAllFieldsFilled = this.checkFilledInfo()
      if (areAllFieldsFilled) isGoodToGo = true
      else isGoodToGo = false

      return isGoodToGo
    },

    /** Update at @param EAKey new info */
    saveDealerAccountDetails (newAccountDetails, EAKey) {
       db.ref('users').child('dealers').child(EAKey).update(newAccountDetails)
    },

    /** Set the @param listDeal in @firebase users/transporters/uid */
    setTransporterDealerLinkList (isLinked) {
      let tempObject = {}

      this.transpLinkList.forEach(transporter => {
        tempObject[transporter.uid] = {
          'linked': isLinked,
          'name': transporter.text
        }
      })

      return tempObject
    },

    /** For each account in @param transporterDealerLinkList, add the currently editted account as a link */
    saveTransporterDealerLinkList (transporterDealerLinkList, EAKey, isLinked) {
      for (const key in transporterDealerLinkList) {
        if (Object.hasOwnProperty.call(transporterDealerLinkList, key)) {
          const obj = {}
          obj[EAKey] = {
            'linked': isLinked,
            'name': this.edittedAccount.name
          }
          db.ref('users').child('transporters').child(key).child('listDeal').update(obj)
        }
      }
    },

    /** Update an account in @PendingCreation @Firebase */
    updatePendingAccountFB (EAKey, newStatus) {
      db.ref('users').child('pending-creation').child(EAKey).update({
        'status': newStatus,
        'transporterUid': this.companyUID
      })
    },

    updateAccountInfoFB (EAKey, updatedAccountInfo) {
      db.ref('users').child('pending-creation').child(EAKey).update(updatedAccountInfo)
    },

    /** Send an email to the created account that they are pogging */
    sendEmail (emailTarget) {
      let emailObj = {
        to: emailTarget,
        subject: 'Account Created',
        text: 'Hi, <br><br> Your account is now ready to use.<br><br>Thank you,<br>Keep N Eye'
      }

      db.ref('email-request4').push().set(emailObj)
    },

    /** On selecting a transporter from the dropdown-select */
    onTransporterSelect (transporterList) {
      this.transpLinkList = transporterList
    },

    /** On removing a transporter from the dropdown-select */
    onRemoveTranspLink (removedTransporterUID) {
      let currentTransporterList = this.getTransporterLinks

      // let isPropertyExist = currentTransporterList.hasOwnProperty(removedTransporterUID)
      for (const key in currentTransporterList) {
        if (Object.hasOwnProperty.call(currentTransporterList, key)) {
          if (key === removedTransporterUID) {
            document.getElementById(removedTransporterUID).className = 'link_removed-opacity m-0'
            delete currentTransporterList[removedTransporterUID]
          }
        }
      }
    },

    /** If Edit modal is closed, restore the transporter link list
     * Called from @Vue TransporterAccount.vue
     * Weird problem (Fixed with this $refs call): If a user removes a link but doesn't confirm the changes (closes out of the modal)
     * and comes back to the same account, the link will not appear anymore until they refresh the page or save the changes.
     * I don't understand why this strong connection happens.
     */
    restoreTransporterLinkData () {
      this.edittedAccount.liste = this.memoryTransptLinkList
    },

    /** Pass through each key attribute in @param userInfo and check if its value is empty
     *  If it's empty, update @param areAllFieldsFilled to false
     */
    checkFilledInfo () {
      let areAllFieldsFilled = true

      for (const key in this.isUserInfoFilled) {
        if (Object.hasOwnProperty.call(this.isUserInfoFilled, key)) {
          const element = this.userInfo[key]
          if (element === '') {
            this.isUserInfoFilled[key] = true // For input fields that are missing
            areAllFieldsFilled = false
            this.setErrorMessage('missing-fields')
          } else this.isUserInfoFilled[key] = false
        }
      }

      return areAllFieldsFilled
    },

    /** Some error types are given by Firebase's account auth system */
    setErrorMessage (errorType) {
      switch (errorType) {
        case 'auth/invalid-email':
          this.errorMessage = this.$t('Account.modal.email_error')
          this.isUserInfoFilled.email = true
          break

        case 'auth/weak-password':
          this.errorMessage = this.$t('Account.modal.password_error')
          this.isUserInfoFilled.password = true
          break

        case 'missing-fields':
          this.errorMessage = this.$t('Account.modal.missing_fields_error')

          break
        default:
          this.errorMessage = this.$t('Account.modal.default_error')
          break
      }
    },

    /** When custom-google-autocomplete receives an emit from @emits Custom-GoogleAutocomplete.vue */
    setEmptyGoogleFields (info) {
      this.isGoogleInfoEmpty = info
    },

    /** When edit modal is opened set @param edittedAccount as @param userInfo */
    userInfoIsEdit () {
      for (const key in this.edittedAccount) {
        if (Object.hasOwnProperty.call(this.edittedAccount, key)) {
          this.userInfo[key] = this.edittedAccount[key]
        }
      }

      this.setBackupTranspLinkList()
    },

    /** Save a backup of the transporter link list of the editted account.
     * Needed because some problems from closing the modal while a transporter is removed
     * (But you can't actually remove a transporter in this version lul)
     */
    setBackupTranspLinkList () {
      if (!this.userInfo.liste) return

      for (const key in this.userInfo.liste) {
        if (Object.hasOwnProperty.call(this.userInfo.liste, key)) {
          this.memoryTransptLinkList[key] = this.userInfo.liste[key]
        }
      }
    },

    /** Set the currently logged in account that is approving accounts as a transporter link
     * This is something done by default lul
    */
    setTransporterListFromParent (companyUID) {
      this.transporterList.find((o) => {
        if (companyUID === o['.key']) {
          this.transpLinkList = [{
            'text': o.name,
            'uid': o['.key'],
            'value': 1
          }]
        }
      })
    }
  },
  computed: {
    getTransporterLinks () {
      const tempListe = this.userInfo.liste

      if (!tempListe) {
        let errorMessage = []
        return errorMessage
      }

      return tempListe
    },

    transpSelectOptions () {
      if (!this.transporterList) return
      let transpArray = []

      this.transporterList.forEach((transporter, i) => {
        transpArray.push({
          value: i,
          text: transporter.name,
          uid: transporter['.key']
        })
      })

      return transpArray
    }
  }
}
</script>

<style scoped>
  .main {
    display: flex;
    justify-content: center;
    flex-flow: row;
  }

  .sub-container {
    min-width: 25%;
    max-width: 25%;
  }

  .button-container {
    display: flex;
    justify-content: space-between;
  }

  img {
    min-width: 200px;
    max-width: 50%;
    height: auto;
  }

  .red-text {
    color: rgb(220, 0, 0);
  }

  .red {
    border-radius: 3px;
    border: 1px solid red !important;
  }
  label {
    white-space: nowrap;
  }

  .link_removed-opacity {
    opacity: 0.5;
  }
</style>
